/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */

import {FilterVue} from '@/shell/lists/filter/FilterVue';
import {
  CompositeFilterCriterion,
  FilterCriterion, FilterId,
  FilterValue
} from '@/shell/lists/filter/FilterValue';
import Component from 'vue-class-component';

/**
 * A base class for composite filters -- filters that derive their value from nested filter(s).
 *
 * An easy way to embed a subfilter inside of this component is to have a subfilter's `value` prop
 * bound to the value of this component, and have its `input` event invoke this component's
 * `nestedFilterValueUpdated()` method.
 */
@Component
export class CompositeFilterBase<I> extends FilterVue<I, CompositeFilterCriterion<I>> {
  protected get filterId(): FilterId {
    return 'CompositeFilterBase';
  }

  /**
   * Has to be invoked by nested filters when their value changes
   *
   * @param newNestedFilterValue a new nested filter's value
   */
  handleSubfilterInput(
      newNestedFilterValue: FilterValue<I, FilterCriterion<I>>
  ): void {
    let compositeFilterCriterion = this.criterion || null;
    if (compositeFilterCriterion === null) {
      compositeFilterCriterion = new CompositeFilterCriterion<I>([]);
    }
    this.criterion =
        compositeFilterCriterion.updateNestedFilterValue(newNestedFilterValue) as
            CompositeFilterCriterion<I>;
  }

  /**
   * @return `true`, if this filter (any of its subfilters) has any condition set that might cause
   *         some of the records to be not accepted, and `false` if this filter is currently in
   *         a state that accepts all records.
   */
  protected get active(): boolean {
    return !!this.value && !this.value.acceptsAnyItem;
  }

  /**
   * Moves this filter into a state that accepts all records.
   */
  protected clearFilters(): void {
    this.criterion = null;
  }
}
